import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { filter } from 'lodash';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import Fa from 'react-fontawesome';
import { Link, Switch, Route, withRouter } from 'react-router-dom';

import ProgressBar from '../../../../components/progress-bar';
import ConfirmButton from '../../../../components/confirm-button';
import OrganizationResyncButton from '../../../../components/organization-resync';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { loadMetaData, updateMetaData } from '../../SetupActions';
import SyncFrequencyForm from '../../components/sync-frequency-form';
import CustomFieldDetailPage from './CustomFieldDetailPage';
import AddCustomObjectButton from './AddCustomObjectButton';

class CustomFieldPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      loading: true,
      errors: null,
      results: [],
      deleting: {},
    };
    this.deleteObject = this.deleteObject.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
  }

  componentDidMount() {
    this.setState({ loading: true, loaded: false, errors: null, results: [] }, () => {
      this.props.dispatch(loadMetaData({ syncRequired: true }))
        .then(
          (results) => {
            this.setState({ loading: false, loaded: false, errors: null, results });
          },
          (errors) => {
            this.setState({ loading: false, loaded: false, errors, results: [] });
          },
        );
    });
  }

  handleSuccess(results) {
    this.setState({ results });
  }

  deleteObject(obj) {
    const id = obj._id;
    const { deleting } = this.state;
    if (!deleting[id]) {
      this.setState({ deleting: { ...deleting, [id]: true } }, () => {
        this.props.dispatch(updateMetaData(obj.name, { sync_required: false }))
          .then(
            () => {
              const newResults = filter(this.state.results, r => r._id !== id);
              this.setState({ deleting: { ...deleting, [id]: false }, results: newResults });
            },
            (error) => {
              // TODO: Error message does not get returned and a ERR_HTTP_HEADERS_SENT gets thrown
              this.context.addNotification(<div>{error.statusText}</div>, { type: 'error', autoClose: false });
              this.setState({ deleting: { ...deleting, [id]: false } });
            },
          );
      });
    }
  }

  renderLoading() {
    return (
      <div>
        <p><em>One moment please...</em></p>
        <p />
        <ProgressBar />
      </div>
    );
  }

  renderInstructions() {
    return (
      <div className="well well-sm">
        <p>The table below displays the objects defined on your organization that have been synced to TruVoice Automation.</p>
      </div>
    );
  }

  renderErrors() {
    const { errors } = this.state;
    if (!errors) {
      return null;
    }
    const message = errors.data ? errors.data.message : errors.message;
    return (
      <div className="alert alert-danger">
        {message}
      </div>
    );
  }

  renderResults() {
    const { results, deleting } = this.state;
    const { organization } = this.props;

    if (!results || results.length === 0) {
      return (
        <div className="alert alert-danger">No data found</div>
      );
    }

    const hasNoneSystemObjects = filter(results, r => !r.is_system_object).length > 0;
    let canAdd = true || organization.type === 'force';
    const isManual = organization.manual_sync;
    if (isManual) {
      canAdd = false;
    }
    return (
      <div>
        <div className="row">
          <div className="col-xs-6">
            <h2 className="page-sub-header">
              <span>Sync Settings</span>
            </h2>
          </div>
          {canAdd &&
            <div className="col-xs-6">
              <div className="pull-right" style={{ paddingTop: 15 }}>
                <OrganizationResyncButton
                  btnTitle="Sync Now"
                  organization={organization}
                  showConfirm={false}
                />
                {' '}
                <AddCustomObjectButton
                  onSuccess={this.handleSuccess}
                />
              </div>
            </div>
          }
        </div>
        <p />
        {!isManual && <SyncFrequencyForm />}
        {this.renderInstructions()}
        <table className="table table-bordered table-striped table-hover">
          <thead>
            <tr>
              <th width="20">&nbsp;</th>
              <th>Object</th>
              <th>API</th>
              {hasNoneSystemObjects  && !isManual && <th className="text-center" width="80">Action</th>}
            </tr>
          </thead>
          <tbody>
            {results.map((result) => {
              const isDeleting = deleting[result._id] === true;
              return (
                <tr key={result._id}>
                  <td className="text-center">
                    <Fa name="star" className={result.is_system_object ? 'text-warning' : 'invisible'} />
                  </td>
                  <td>
                    <Link to={`/setup/sync-settings/${result.name}`}>{result.label}</Link>
                  </td>
                  <td><code>{result.name}</code></td>
                  {hasNoneSystemObjects && !isManual &&
                    <td>
                      {!result.is_system_object &&
                        <ConfirmButton
                          size="sm"
                          disabled={isDeleting}
                          onConfirm={() => { this.deleteObject(result); }}
                        >
                          <Fa name={isDeleting ? 'spinner' : 'trash'} spin={isDeleting} /> Delete
                        </ConfirmButton>
                      }
                    </td>
                  }
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderContent() {
    const { loading, loaded, errors } = this.state;
    if (loading) {
      return this.renderLoading();
    }
    if (!loaded && errors) {
      return this.renderErrors();
    }
    return this.renderResults();
  }

  render() {
    const { organization, location: { pathname } } = this.props;
    const isDemo = organization && organization.is_demo;
    return (
      <div>
        <Helmet title="Sync Settings" />
        <div className="container">
          <div>
            {!isDemo &&
              <Switch>
                <Route key={`${organization._id}|${pathname}`} exact path="/setup/sync-settings/:objectName" component={CustomFieldDetailPage} />
                {pathname === '/setup/sync-settings' && this.renderContent()}
              </Switch>
            }
            {isDemo && 
              <div className="alert alert-warning">
                This is a demo organization, please switch back to the original organization to change the amound field mapping.
              </div>
            }
          </div>
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
  };
}

CustomFieldPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  organization: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    type: PropTypes.string,
    is_demo: PropTypes.bool,
    last_synced_date: PropTypes.string,
    last_synced_duration: PropTypes.number,
    next_sync_date: PropTypes.string,
  }).isRequired,
};
CustomFieldPage.contextTypes = {
  router: PropTypes.object,
  addNotification: PropTypes.func.isRequired,
};

export default withRouter(connect(mapStateToProps)(CustomFieldPage));
